/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BEM } from "@adapptio/lib-components";
import React, { FC, useCallback, useContext, MouseEvent } from "react";
import { Icon, ICON_SIZE } from "../Icon";
import { FieldSectionContext } from "./FieldSection";

export interface IFieldHeaderProps {
	/** Custom class name */
	className?: string;
}

export const FieldHeader: FC<IFieldHeaderProps> = ({ children, className }) => {
	const sectionCtx = useContext(FieldSectionContext);

	const handleCollapseToggle = useCallback(
		(ev: MouseEvent) => {
			if (sectionCtx?.collapsible && sectionCtx?.toggleCollapsed) {
				ev.stopPropagation();
				sectionCtx.toggleCollapsed();
			}
		},
		[ sectionCtx?.collapsible, sectionCtx?.toggleCollapsed ]
	);

	const baseClass = BEM("edt-field-header", null, {
		"is-collapsible": sectionCtx?.collapsible
	});

	if (className) {
		baseClass.add(className);
	}

	return (
		<div className={baseClass.toClassName()} onClick={handleCollapseToggle}>
			{children}
			{sectionCtx && sectionCtx.collapsible ? (
				<button
					className={baseClass
						.element("collapse-arrow", { "is-collapsed": sectionCtx.isCollapsed })
						.add("edt-field-header__button")
						.toClassName()}
					onClick={handleCollapseToggle}
				>
					<Icon name="mdi/chevron-down" size={ICON_SIZE.XSMALL} />
				</button>
			) : null}
		</div>
	);
};
